/* eslint-disable max-lines-per-function */
import type { CreateEstimateParams } from '@/domain/models/development.js';
import type {
    ApproveDocumentation,
    ApproveEstimate,
    ApproveSolicitation,
    ApproveUcValidation,
    AssignTechLead,
    CloseWithoutUc,
    ConfirmNoJiraPair,
    CreateEstimate,
    CreateSolicitation,
    DeleteEf,
    DownloadCfEvidence,
    DownloadDocument,
    DownloadEf,
    DownloadUcEvidence,
    GetAiAnalysisProgress,
    GetPbiDecomposition,
    GetSolicitationEvidenceDownloadUrl,
    LinkJiraEpic,
    ListCfEvidences,
    ListJiraEpics,
    ListSolicitationEvidences,
    ListUcEvidences,
    LoadAudit,
    MakeDeveloperLead,
    ReclassifySolicitation,
    RegisterDevEnd,
    RejectDocumentation,
    RejectEstimate,
    RejectSolicitation,
    ReleaseToUc,
    RemoveDeveloper,
    RemoveSolicitationEvidence,
    RemoveValidationEvidence,
    RequestAiAnalysis,
    ResolveSolicitation,
    RetryJiraSync,
    RetryPbiDecomposition,
    ReturnEstimateToAnalysis,
    SendToDevelopment,
    SetDevelopers,
    SubmitEstimate,
    ToggleCutoverNotRequired,
    UnlinkJiraEpic,
    UploadDocument,
    UploadEf,
    UploadSolicitationEvidence,
    UploadValidationEvidence
} from '@/domain/use-cases/developments.js';
import { BaseControllerImpl } from '@/presentation/controllers/base/implementation.js';

export class DevelopmentDetailController extends BaseControllerImpl {
    constructor(
        private readonly uploadEfUseCase: UploadEf,
        private readonly deleteEfUseCase: DeleteEf,
        private readonly downloadEfUseCase: DownloadEf,
        private readonly requestAiAnalysisUseCase: RequestAiAnalysis,
        private readonly sendToDevelopmentUseCase: SendToDevelopment,
        private readonly createEstimateUseCase: CreateEstimate,
        private readonly approveEstimateUseCase: ApproveEstimate,
        private readonly rejectEstimateUseCase: RejectEstimate,
        private readonly submitEstimateUseCase: SubmitEstimate,
        private readonly returnEstimateToAnalysisUseCase: ReturnEstimateToAnalysis,
        private readonly assignTechLeadUseCase: AssignTechLead,
        private readonly setDevelopersUseCase: SetDevelopers,
        private readonly removeDeveloperUseCase: RemoveDeveloper,
        private readonly makeDeveloperLeadUseCase: MakeDeveloperLead,
        private readonly uploadDocumentUseCase: UploadDocument,
        private readonly downloadDocumentUseCase: DownloadDocument,
        private readonly registerDevEndUseCase: RegisterDevEnd,
        private readonly toggleCutoverNotRequiredUseCase: ToggleCutoverNotRequired,
        private readonly approveDocumentationUseCase: ApproveDocumentation,
        private readonly rejectDocumentationUseCase: RejectDocumentation,
        private readonly uploadValidationEvidenceUseCase: UploadValidationEvidence,
        private readonly listCfEvidencesUseCase: ListCfEvidences,
        private readonly listUcEvidencesUseCase: ListUcEvidences,
        private readonly removeValidationEvidenceUseCase: RemoveValidationEvidence,
        private readonly downloadCfEvidenceUseCase: DownloadCfEvidence,
        private readonly downloadUcEvidenceUseCase: DownloadUcEvidence,
        private readonly releaseToUcUseCase: ReleaseToUc,
        private readonly approveUcValidationUseCase: ApproveUcValidation,
        private readonly closeWithoutUcUseCase: CloseWithoutUc,
        private readonly createSolicitationUseCase: CreateSolicitation,
        private readonly approveSolicitationUseCase: ApproveSolicitation,
        private readonly rejectSolicitationUseCase: RejectSolicitation,
        private readonly resolveSolicitationUseCase: ResolveSolicitation,
        private readonly reclassifySolicitationUseCase: ReclassifySolicitation,
        private readonly listSolicitationEvidencesUseCase: ListSolicitationEvidences,
        private readonly uploadSolicitationEvidenceUseCase: UploadSolicitationEvidence,
        private readonly removeSolicitationEvidenceUseCase: RemoveSolicitationEvidence,
        private readonly getSolicitationEvidenceDownloadUrlUseCase: GetSolicitationEvidenceDownloadUrl,
        private readonly loadAuditUseCase: LoadAudit,
        private readonly getPbiDecompositionUseCase: GetPbiDecomposition,
        private readonly getAiAnalysisProgressUseCase: GetAiAnalysisProgress,
        private readonly retryPbiDecompositionUseCase: RetryPbiDecomposition,
        private readonly listJiraEpicsUseCase: ListJiraEpics,
        private readonly linkJiraEpicUseCase: LinkJiraEpic,
        private readonly confirmNoJiraPairUseCase: ConfirmNoJiraPair,
        private readonly retryJiraSyncUseCase: RetryJiraSync,
        private readonly unlinkJiraEpicUseCase: UnlinkJiraEpic
    ) {
        super();
    }

    public async uploadEf(developmentId: string, file: File) {
        return this.uploadEfUseCase.execute(developmentId, file);
    }

    public async deleteEf(developmentId: string) {
        return this.deleteEfUseCase.execute(developmentId);
    }

    public async downloadEf(developmentId: string, version?: number) {
        return this.downloadEfUseCase.execute(developmentId, version);
    }

    public async requestAiAnalysis(developmentId: string) {
        return this.requestAiAnalysisUseCase.execute(developmentId);
    }

    public async sendToDevelopment(developmentId: string) {
        return this.sendToDevelopmentUseCase.execute(developmentId);
    }

    public async createEstimate(developmentId: string, params: CreateEstimateParams) {
        return this.createEstimateUseCase.execute(developmentId, params);
    }

    public async approveEstimate(developmentId: string) {
        return this.approveEstimateUseCase.execute(developmentId);
    }

    public async rejectEstimate(developmentId: string, reasonId: string) {
        return this.rejectEstimateUseCase.execute(developmentId, reasonId);
    }

    public async submitEstimate(developmentId: string) {
        return this.submitEstimateUseCase.execute(developmentId);
    }

    public async returnEstimateToAnalysis(developmentId: string, reason: string, notes?: string) {
        return this.returnEstimateToAnalysisUseCase.execute(developmentId, reason, notes);
    }

    public async assignTechLead(developmentId: string, techLeadId: string) {
        return this.assignTechLeadUseCase.execute(developmentId, techLeadId);
    }

    public async setDevelopers(developmentId: string, developers: Array<{ userId: string; isLead: boolean }>) {
        return this.setDevelopersUseCase.execute(developmentId, developers);
    }

    public async removeDeveloper(developmentId: string, developerId: string) {
        return this.removeDeveloperUseCase.execute(developmentId, developerId);
    }

    public async makeDeveloperLead(developmentId: string, developerId: string) {
        return this.makeDeveloperLeadUseCase.execute(developmentId, developerId);
    }

    public async uploadDocument(developmentId: string, type: string, file: File) {
        return this.uploadDocumentUseCase.execute(developmentId, type, file);
    }

    public async downloadDocument(developmentId: string, documentId: string) {
        return this.downloadDocumentUseCase.execute(developmentId, documentId);
    }

    public async registerDevEnd(developmentId: string, endDate: string) {
        return this.registerDevEndUseCase.execute(developmentId, endDate);
    }

    public async toggleCutoverNotRequired(developmentId: string, cutoverNotRequired: boolean) {
        return this.toggleCutoverNotRequiredUseCase.execute(developmentId, cutoverNotRequired);
    }

    public async approveDocumentation(developmentId: string) {
        return this.approveDocumentationUseCase.execute(developmentId);
    }

    public async rejectDocumentation(developmentId: string, reasonId: string, justification: string) {
        return this.rejectDocumentationUseCase.execute(developmentId, reasonId, justification);
    }

    public async uploadValidationEvidence(developmentId: string, file: File, type: 'cf' | 'uc') {
        return this.uploadValidationEvidenceUseCase.execute(developmentId, file, type);
    }

    public async removeValidationEvidence(developmentId: string, type: 'cf' | 'uc', evidenceId: string) {
        return this.removeValidationEvidenceUseCase.execute(developmentId, type, evidenceId);
    }

    public async listCfEvidences(developmentId: string) {
        return this.listCfEvidencesUseCase.execute(developmentId);
    }

    public async listUcEvidences(developmentId: string) {
        return this.listUcEvidencesUseCase.execute(developmentId);
    }

    public async downloadCfEvidence(developmentId: string, docId: string) {
        return this.downloadCfEvidenceUseCase.execute(developmentId, docId);
    }

    public async downloadUcEvidence(developmentId: string, docId: string) {
        return this.downloadUcEvidenceUseCase.execute(developmentId, docId);
    }

    public async releaseToUc(developmentId: string) {
        return this.releaseToUcUseCase.execute(developmentId);
    }

    public async approveUcValidation(developmentId: string) {
        return this.approveUcValidationUseCase.execute(developmentId);
    }

    public async closeWithoutUc(developmentId: string) {
        return this.closeWithoutUcUseCase.execute(developmentId);
    }

    public async createSolicitation(developmentId: string, params: { type: 'BUG' | 'AJUSTE_MINOR'; title: string; description: string; stepsToReproduce?: string }) {
        return this.createSolicitationUseCase.execute(developmentId, params);
    }

    public async approveSolicitation(developmentId: string, solicitationId: string) {
        return this.approveSolicitationUseCase.execute(developmentId, solicitationId);
    }

    public async rejectSolicitation(developmentId: string, solicitationId: string, reasonId: string, notes?: string) {
        return this.rejectSolicitationUseCase.execute(developmentId, solicitationId, reasonId, notes);
    }

    public async resolveSolicitation(developmentId: string, solicitationId: string, notes: string) {
        return this.resolveSolicitationUseCase.execute(developmentId, solicitationId, notes);
    }

    public async reclassifySolicitation(developmentId: string, solicitationId: string, newType: string, notes: string) {
        return this.reclassifySolicitationUseCase.execute(developmentId, solicitationId, newType, notes);
    }

    public async listSolicitationEvidences(developmentId: string, solicitationId: string) {
        return this.listSolicitationEvidencesUseCase.execute(developmentId, solicitationId);
    }

    public async uploadSolicitationEvidence(developmentId: string, solicitationId: string, file: File) {
        return this.uploadSolicitationEvidenceUseCase.execute(developmentId, solicitationId, file);
    }

    public async removeSolicitationEvidence(developmentId: string, solicitationId: string, docId: string) {
        return this.removeSolicitationEvidenceUseCase.execute(developmentId, solicitationId, docId);
    }

    public async getSolicitationEvidenceDownloadUrl(developmentId: string, solicitationId: string, docId: string) {
        return this.getSolicitationEvidenceDownloadUrlUseCase.execute(developmentId, solicitationId, docId);
    }

    public async loadAudit(developmentId: string) {
        return this.loadAuditUseCase.execute(developmentId);
    }

    public async getPbiDecomposition(developmentId: string) {
        return this.getPbiDecompositionUseCase.execute(developmentId);
    }

    public async getAiAnalysisProgress(developmentId: string) {
        return this.getAiAnalysisProgressUseCase.execute(developmentId);
    }

    public async retryPbiDecomposition(developmentId: string) {
        return this.retryPbiDecompositionUseCase.execute(developmentId);
    }

    public async listJiraEpics(developmentId: string) {
        return this.listJiraEpicsUseCase.execute(developmentId);
    }

    public async linkJiraEpic(developmentId: string, epicKey: string) {
        return this.linkJiraEpicUseCase.execute(developmentId, epicKey);
    }

    public async unlinkJiraEpic(developmentId: string) {
        return this.unlinkJiraEpicUseCase.execute(developmentId);
    }

    public async confirmNoJiraPair(developmentId: string) {
        return this.confirmNoJiraPairUseCase.execute(developmentId);
    }

    public async retryJira(developmentId: string) {
        return this.retryJiraSyncUseCase.execute(developmentId);
    }
}
